import React from 'react';
import {Button, message, Modal, Spin, Upload} from "antd";
import styles from "./ImportModal.module.css";
import {ExclamationCircleOutlined, UploadOutlined} from "@ant-design/icons";
import {SchematicList} from "../SchematicList/SchematicList";
import LocalStorageService from "../../../localStorage-service";
import APIService from "../../services/APIService";
import {connect} from "react-redux";
import {
    removeLoader, setActivePreviewSymbol,
    setGraph, setLoader, setSchematicDocument,
    setSidebarVisibility, setSymbolTypes, createDraft, setActiveSchematicType
} from "../../../store/Schematic_store/actions";

const showMessage = (typeofMessage, content) => {
    message[typeofMessage](content)
};

const {confirm} = Modal;

class ImportModal extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            confirmLoading: false,
            schematic_list: [],
            showLoader: false,
        }
        this.getAllSchematicDocumentsByUser();
    }


    getAllSchematicDocumentsByUser = () => {
        APIService.getSchematicsList().then(result => {
            this.setState({schematic_list: result.list});
        });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.visible !== prevProps.visible && this.props.visible) {
            this.getAllSchematicDocumentsByUser()
        }
    }

    importSchematicFromDevice = (file) => {
        const {schematic_list} = this.state;
        this.setState({showLoader: true});
        const fr = new FileReader();
        fr.onload = (e) => {
            try {
                const jsonIndex = file.name.indexOf('.json');
                const fileName = file.name.slice(0, jsonIndex);
                const json = JSON.parse(e.target.result);
                /**
                 * Check if the json has invalid format
                 */
                if (!json || !json.pages || typeof json.schematic_type_id !== "number" || typeof json.activePage !== "number") {
                    throw new Error('Imported schema is broken');
                }
                if (schematic_list.find(schematic => schematic.document_name === fileName)) {
                    confirm({
                        title: `${fileName} already exists in your cloud files. Would you like to overwrite it?`,
                        icon: <ExclamationCircleOutlined/>,
                        content: '',
                        okText: "Yes, overwrite",
                        cancelText: `No, I'll save it manually`,
                        onOk: () => {
                            this.backupSchematicToCloud(json, fileName);
                        },
                        onCancel: () => {
                            this.backupSchematicToCloud(json, 'Draft project');
                        }
                    });
                } else {
                    this.backupSchematicToCloud(json, fileName);
                }
            } catch (e) {
                showMessage('error', e.message);
                this.setState({showLoader: false});
            }
        }
        fr.readAsText(file);
        return false;
    }
    backupSchematicToCloud = (json, fileName) => {
        const {onClose} = this.props;
        const {schematic_type_id} = json;
        APIService.saveSchematic({json, document_name: fileName, schematic_type_id}).then(result => {
            this.setSchematicLocally(result);
            this.setState({showLoader: false});
        }).catch((e) => {
            showMessage('error', e.message);
            this.setState({showLoader: false});
            onClose()
        })
    }

    importSchematicFromCloud = (document) => {
        const {onClose} = this.props;
        this.setState({showLoader: true});
        APIService.getSchematic(document.id).then(result => {
            this.setSchematicLocally(result);
            this.setState({showLoader: false});
        }).catch((e) => {
            showMessage('error', e.message);
            this.setState({showLoader: false});
            onClose()
        })
    }

    setSchematicLocally = (result) => {
        const {onClose, setLoader, removeLoader, setSymbolTypes, setActiveSymbol} = this.props;
        const schematic_type_id = LocalStorageService.getSchematicTypeId();
        /**
         * We are importing schema of different type, need to refresh symbols
         */
        if (schematic_type_id !== result.schematic_type_id) {
            setLoader('Fetching symbols...');
            APIService.getTypesSymbols(result.schematic_type_id).then(types => {
                setActiveSymbol(null);
                setSymbolTypes(types);
                removeLoader('Fetching symbols...');
                this.dispatchNewSchematicData(result);
                onClose();
            }).catch(() => removeLoader('Fetching symbols...'));
        } else {
            this.dispatchNewSchematicData(result);
            onClose();
        }
    };

    dispatchNewSchematicData = (schematicData) => {
        const {document_name, data} = schematicData;
        const {setSidebarVisibility, setSchematicDocument, setGraphToStore, setActiveSchematicType} = this.props;
        setGraphToStore(null);
        setSchematicDocument({document_name, data});
        setSidebarVisibility(false);
        LocalStorageService.setSchematic(schematicData);
        APIService.getActiveSchematicType(schematicData.schematic_type_id).then(activeSchematicType => {
            setActiveSchematicType(activeSchematicType);
        })
    }

    removeSchematicDocument = (item) => {
        const {schematic_list} = this.state;
        const {createDraft} = this.props;
        this.setState({showLoader: true});
        const currentSchematicId = LocalStorageService.getSchematicId();
        if (currentSchematicId !== item.id) {
            APIService.removeSchematic(item.document_name).then(userSchematic => {
                const updateList = schematic_list.filter(schematic => schematic.document_name !== item.document_name);
                this.setState({schematic_list: updateList, showLoader: false});
            }).catch(() => this.setState({showLoader: false}))
        } else {
            confirm({
                title: `You are attempting to delete the the schematic you're currently working on. Would you like to proceed?`,
                icon: <ExclamationCircleOutlined/>,
                content: '',
                okText: "Continue",
                cancelText: `Cancel`,
                onOk: () => {
                    APIService.removeSchematic(item.document_name).then(userSchematic => {
                        const updateList = schematic_list.filter(schematic => schematic.document_name !== item.document_name);
                        this.setState({schematic_list: updateList, showLoader: false});
                        /**
                         * if we removed the one we're working on - create draft automatically
                         */
                        createDraft();
                    }).catch(() => this.setState({showLoader: false}));
                },
                onCancel: () => {
                    this.setState({showLoader: false});
                }
            });
        }
    }

    render() {
        const {confirmLoading, showLoader, schematic_list} = this.state;
        const {visible, onClose} = this.props;
        return (
            <Modal
                title='Schematic List'
                visible={visible}
                confirmLoading={confirmLoading}
                onCancel={() => onClose()}
                footer={[
                    <Upload className={styles.uploadBtn} accept={'.json'} showUploadList={false}
                            beforeUpload={this.importSchematicFromDevice}>
                        <Button type="primary" icon={<UploadOutlined/>} size={'medium'}>
                            Select from device
                        </Button>
                    </Upload>
                    ,
                    <Button key="back" onClick={() => onClose()}>
                        Cancel
                    </Button>
                ]}
            >
                <SchematicList onImport={this.importSchematicFromCloud}
                               removeSchematicDocument={this.removeSchematicDocument}
                               schematic_list={schematic_list}/>
                <Spin size={'large'}
                      tip="Loading..."
                      style={{position: "absolute"}}
                      className={styles.spinning}
                      spinning={showLoader}/>
            </Modal>
        )
    }
}

function mapStateToProps({schematicReducers: {symbolsTypes}}) {
    return {
        symbolsTypes
    }
}

function mapDispatchToProps(dispatch) {
    return {
        setGraphToStore: (graph) => {
            dispatch(setGraph(graph))
        },
        setSidebarVisibility: (value) => {
            dispatch(setSidebarVisibility(value))
        },
        setSchematicDocument: (schematicDocument) => {
            dispatch(setSchematicDocument(schematicDocument))
        },
        setLoader: (loader) => {
            dispatch(setLoader(loader))
        },
        removeLoader: (loader) => {
            dispatch(removeLoader(loader))
        },
        setSymbolTypes: (symbolTypes) => {
            dispatch(setSymbolTypes(symbolTypes))
        },
        setActiveSymbol: (symbol) => {
            dispatch(setActivePreviewSymbol(symbol))
        },
        setActiveSchematicType: (activeSchematicType) => {
            dispatch(setActiveSchematicType(activeSchematicType))
        },
        createDraft: () => {
            dispatch(createDraft());
        }
    }
}


export default connect(mapStateToProps, mapDispatchToProps)(ImportModal);